import { Box, Collapse, MenuItem, Select, TextField, Typography, Button, IconButton, Drawer, Popover, useTheme, CircularProgress, Tooltip, Card } from '@mui/material'
import React, { useState, useRef } from 'react'
import { Icons, attachmentThumbnails, convertToCDNLink, getFileSizeFromBytes } from '../../../utils/utilities'
import { observer } from 'mobx-react'
import api from '../../../service/api'
import { toast } from 'react-toastify'
import { ExpandMore } from '@mui/icons-material'
import EditSetting from './EditSetting'
import CustomSwitch from '../../common/CustomSwitch'
import DocumentViewer from '../../common/DocumentViewer'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomChip from '../../common/CustomChip'

const validateAsNumber = ( val ) => {
    try {
        if ( val !== '' )
            return !Number.isNaN( parseInt( val ) )
        else
            return true
    } catch ( err ) {
        return false
    }
}

const getNumericValue = ( val ) => {
    return parseInt( val )
}

const getInitialSettingValue = ( setting ) => {
    const { setting_data_type, value = '' } = setting

    switch ( setting_data_type ) {
        case 'boolean':
            return value === "true"

        case 'multiple':
            return value.split( "," ).map( i => i.trim() )

        case 'file':
            try {
                return value ? JSON.parse( value ) : []
            } catch ( e ) {
                console.error( "Failed to parse file setting JSON:", e )
                return []
            }

        default:
            return value
    }
}

const ControlPanelSettingBox = observer( ( { isParent = true, setting, getSelectedSettings } ) => {
    const [isSettingActive, setIsSettingActive] = useState( setting.setting_data_type === 'boolean' ? setting.value === "true" : Boolean( setting.value ) )
    const [settingValue, setSettingValue] = useState( () => getInitialSettingValue( setting ) )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [settingForEdit, setSettingForEdit] = useState( null )
    const [previewFile, setPreviewFile] = useState( null )
    const [deletingFile, setDeletingFile] = useState( {} )
    const [updating, setUpdating] = useState( false )
    const [anchorEl, setAnchorEl] = useState( null )

    const { palette, border } = useTheme()

    const multipleTypeInputRef = useRef( null )
    const fileInputRef = useRef( null )

    const handleSettingChange = async ( value, type = 'textual' ) => {
        try {
            if ( String( value ) === String( setting.value ) )
                return
            setUpdating( true )
            await api.updateSetting( setting.setting_data_id, type === 'textual' ? { value } : value )
            const newValue = getInitialSettingValue( { ...setting, value } )
            setSettingValue( newValue )
            toast( `${setting.setting_label} updated successfully` )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "An error occured while updating the setting! please try again." )
        } finally {
            setUpdating( false )
        }
    }

    const handleKeyDownOnMultipleTypeInput = async ( event ) => {
        const { code } = event

        if ( code === "Enter" ) {
            event.preventDefault()
            await addNewValueForMultiValuedSetting()
        }
    }

    const addNewValueForMultiValuedSetting = async () => {
        let value = multipleTypeInputRef.current.value
        value = value?.trim()
        if ( !value )
            return

        value = value?.split( "," )?.map( i => i.trim() )
        value = Array.from( new Set( value ) )

        if ( settingValue.some( i => value?.includes( i ) ) ) {
            if ( value.length > 1 )
                return toast( "At least one of the provided value already exists." )
            return toast( "Provided value is already present." )
        }

        const newValue = [...settingValue, ...value].join( "," )

        await handleSettingChange( newValue )

        multipleTypeInputRef.current.value = ""

    }

    const deleteValueFromMultiValuedSetting = async ( valueToRemove ) => {
        const valueIndex = settingValue.findIndex( i => i === valueToRemove )
        if ( valueIndex >= 0 ) {
            const newSettingValue = [...settingValue]
            newSettingValue.splice( valueIndex, 1 )
            await handleSettingChange( newSettingValue.join( "," ) )
        }
    }

    const handleDeleteAttachment = async ( key ) => {
        const fileIndex = settingValue.findIndex( f => f.key === key )
        setDeletingFile( prev => ( { ...prev, [fileIndex]: true } ) )
        try {
            const fileToDelete = settingValue[fileIndex]
            const { data } = await api.deleteSettingAttachment( setting.setting_data_id, { key: fileToDelete.key } )
            toast( `${fileToDelete.name} : Attachment deleted successfully` )
            setSettingValue( data.data )
        } catch ( err ) {
            console.error( err )
            toast( `Failed to delete attachment` )
        } finally {
            setDeletingFile( prev => ( { ...prev, [fileIndex]: false } ) )
        }
    }

    const copySettingKey = ( key ) => {
        navigator.clipboard.writeText( key )
        toast( "Copied key to clipboard" )
    }

    const handleFileChange = async ( event, setting ) => {
        setUpdating( true )
        const files = Array.from( event.target.files )
        const acceptedExtensions = ['pdf', 'docx']
        const existingfiles = []
        let newFilesExists = false
        const formData = new FormData()
        for ( const file of files ) {
            const fileExtension = file.name.split( "." ).pop()
            if ( !acceptedExtensions.includes( fileExtension ) ) {
                toast( 'Only PDF and DOCX files are accepted' )
                fileInputRef.current.value = null
                continue
            }
            const exists = settingValue.some( existingfile => existingfile.name === file.name )
            if ( exists ) {
                existingfiles.push( file.name )
                continue
            }
            newFilesExists = true
            formData.append( 'attachments[]', file, file.name )
        }
        if ( existingfiles.length > 0 ) {
            toast( "The following files already exist: " + existingfiles.join( ", " ) )
        }
        if ( newFilesExists ) {
            try {
                await handleSettingChange( formData, 'file' )
            } catch ( err ) {
                console.error( err )
                toast( 'Failed to upload file' )
            } finally {
                event.target.value = ""
            }
        }
        setUpdating( false )
    }

    const handleViewFile = ( file ) => {
        const cdnLink = convertToCDNLink( file.location )
        setPreviewFile( cdnLink )
        setPreviewingFileDetails( { sample_material_format: file.name, type: file.type } )
    }

    const handleDownloadFile = ( file ) => {
        const link = document.createElement( 'a' )
        link.href = file.location
        link.download = file.name
        link.click()
        link.remove()
    }

    const viewUploadedFiles = ( event, setting ) => {
        setAnchorEl( event.currentTarget )
    }

    const handlePopClose = () => {
        setAnchorEl( null )
    }


    return (
        <Card elevation={1} sx={{ minWidth: "400px", display: "flex", border: border[1] + "99", height: "fit-content", alignItems: 'stretch' }}>
            {settingForEdit && <EditSetting setState={setSettingForEdit} getSelectedSettings={getSelectedSettings} dialogState={settingForEdit ? true : false} setting={settingForEdit} />}
            <Box flexGrow={1}>
                <Box bgcolor={isParent ? palette.contentBg : "none"} minHeight="60px" flexWrap="wrap" gap="10px" flexGrow={1} padding="10px" display="flex" flexDirection={{ xs: "column", lg: "row" }} alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography display="inline-block" fontSize="16px" fontWeight="500">{setting.setting_label} </Typography>
                        <Typography fontSize="10px">{setting.setting_description}</Typography>
                        <Box display="flex" flexWrap="wrap" marginTop="10px" gap={{ md: "10px", xs: "5px" }}>
                            <Button size='small' variant='outlined' color='greyed' onClick={() => { setSettingForEdit( setting ) }} startIcon={Icons.default.EditIcon} > Edit</Button>
                            <Button size='small' variant='outlined' color='greyed' onClick={() => { copySettingKey( setting.setting_key ) }} startIcon={Icons.default.Copy} >Copy Setting Key</Button>
                        </Box>
                    </Box>
                    <Box width={{ xs: "100%", lg: "initial" }} >
                        {setting.setting_data_type === 'boolean' && <CustomSwitch checked={isSettingActive} onChange={( e ) => { handleSettingChange( e.target.checked ); setIsSettingActive( !isSettingActive ) }} />}
                        {( setting.setting_data_type === 'text' ) && <TextField fullWidth size="small" value={settingValue || ""} onBlur={( e ) => handleSettingChange( e.target.value )} onChange={( e ) => setSettingValue( e.target.value )} slotProps={{
                            input: { sx: { overflow: "clip" }, endAdornment: <Typography bgcolor={palette.action.hover} fontSize="10px" fontWeight="600" color="textSecondary" padding="10px 15px 10px 10px" minWidth="30px" textAlign="center" marginRight="-14px">{setting.unit}</Typography> }
                        }} />}

                        {( setting.setting_data_type === 'multiple' ) && <Box >
                            <Box display="flex" gap="10px">
                                <TextField size="small" placeholder='Type new value here...' inputRef={multipleTypeInputRef} onKeyDown={handleKeyDownOnMultipleTypeInput} fullWidth />
                                <Button disabled={updating} onClick={addNewValueForMultiValuedSetting} variant='contained' color='secondary' sx={{ flexShrink: 0 }} size='small' startIcon={updating ? <CircularProgress size={14} /> : Icons.default.AddIcon}>
                                    Add
                                </Button>
                            </Box>
                            <Box marginTop={settingValue?.length > 0 ? "10px" : "0"} display="flex" justifyContent={{ xs: "flex-start", lg: "flex-end" }} flexWrap='wrap' gap="5px" >
                                {settingValue.map( ( val, index ) => {
                                    return (
                                        <CustomChip onEndIconClick={() => deleteValueFromMultiValuedSetting( val )} value={val} disabled={updating} key={index} endIcon={Icons.default.CancelIcon} />
                                    )
                                } )}
                            </Box>
                        </Box>}



                        {setting.setting_data_type === 'number' && <TextField
                            fullWidth type='number'
                            size="small" value={settingValue || ""}
                            onBlur={( e ) => {
                                if ( validateAsNumber( e.target.value ) )
                                    handleSettingChange( getNumericValue( e.target.value ) )
                            }}
                            onChange={( e ) => {
                                if ( validateAsNumber( e.target.value ) )
                                    setSettingValue( e.target.value )
                            }}
                            slotProps={{
                                input: { sx: { overflow: "clip" }, endAdornment: <Typography bgcolor={palette.action.hover} fontSize="10px" fontWeight="600" color="textSecondary" padding="10px 15px 10px 10px" minWidth="30px" textAlign="center" marginRight="-14px">{setting.unit}</Typography> }
                            }}
                        />}
                        {setting.setting_data_type === 'select' && <Select fullWidth size="small" value={settingValue || ""} onChange={( e ) => { handleSettingChange( e.target.value ); setSettingValue( e.target.value ) }}>
                            {setting.setting_options.map( option => (
                                <MenuItem value={option} sx={{ fontSize: "14px" }} dense key={option}>{option}</MenuItem>
                            ) )}
                        </Select>}
                        {setting.setting_data_type === 'file' && (
                            <Box>
                                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={( e ) => handleFileChange( e, setting )} multiple accept='.pdf,.docx' />

                                <Box display="flex" alignItems="center" gap="10px">
                                    {settingValue.length > 0 && (
                                        <Box onClick={viewUploadedFiles} gap="10px" color="secondary.main" width="fit-content" display="flex" alignItems="center" justifyContent="space-between" borderRadius="40px" bgcolor={palette.secondary.light + "22"} border={`1px solid ${palette.secondary.main}`} padding="5px 20px" sx={{ cursor: "pointer", "&:hover": { background: palette.secondary.light + "55" } }}>
                                            <Typography variant="subtitle2" sx={{ cursor: 'pointer' }} >
                                                {settingValue.length} Files uploaded
                                            </Typography>
                                            <ExpandMore />
                                        </Box>
                                    )}
                                    {Array.isArray( settingValue ) && settingValue.length > 0 ? (
                                        <Button sx={{ textTransform: "capitalize" }} component="span" color="greyed" disabled={updating} disableElevation startIcon={updating ? <CircularProgress size={14} /> : Icons.default.AddCircleIcon} onClick={() => fileInputRef.current.click()} >
                                            {updating ? "Uploading..." : "Add more"}
                                        </Button>
                                    ) : (
                                        <Button sx={{ textTransform: "capitalize" }} component="span" color="greyed" disabled={updating} disableElevation startIcon={updating ? <CircularProgress size={14} /> : Icons.default.AddIcon} onClick={() => fileInputRef.current.click()}>
                                            {updating ? "Uploading..." : "Upload Files"}
                                        </Button>
                                    )}
                                </Box>

                                <Popover open={Boolean( anchorEl )} anchorEl={anchorEl} onClose={handlePopClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }} transformOrigin={{ vertical: 'top', horizontal: 'right', }}>
                                    {Boolean( anchorEl ) && Array.isArray( settingValue ) && settingValue.map( ( file, index ) => (
                                        <Box borderBottom={border[1]} key={file.key} display="flex" alignItems="center" gap="10px" sx={{ padding: '10px' }}>
                                            <img style={{ objectFit: "cover" }} width="45px" src={attachmentThumbnails[file.name?.split( "." ).pop()] ? attachmentThumbnails[file.name?.split( "." ).pop()] : attachmentThumbnails["default"]} alt={file.type} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography component="p" variant='subtitle2' fontSize="12px" sx={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant='subtitle2' fontSize="10px">
                                                    {getFileSizeFromBytes( file.size )}
                                                </Typography>
                                            </Box>
                                            <IconButton size='small' onClick={() => handleDownloadFile( file )}>
                                                {Icons.default.DownloadIcon}
                                            </IconButton>
                                            <IconButton size='small' onClick={() => handleViewFile( file )}>
                                                {Icons.default.VisibilityIcon}
                                            </IconButton>
                                            <Tooltip title="Remove this file">
                                                <IconButton color='error' size='small' onClick={() => handleDeleteAttachment( file.key )}>
                                                    {deletingFile[index] ? <DataLoadingSpinner padding='0' size={20} /> : Icons.default.CloseIcon}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ) )}
                                </Popover>
                            </Box>
                        )}
                        {/* {setting.setting_data_type === 'multiple' && <Typography color="GrayText" fontSize="12px" textAlign="right" display="block" variant='subtitle2'>Enter as comma separated values</Typography>} */}
                    </Box>
                </Box>
                <Collapse in={isSettingActive === true && setting.children && setting.children.length > 0}>
                    <Box display="flex" gap="5px" flexDirection="column" padding="10px" marginLeft="5px">
                        {setting?.children?.map( child => {
                            return (
                                <ControlPanelSettingBox isParent={false} key={child.setting_data_id} setting={child} getSelectedSettings={getSelectedSettings} />
                            )
                        } )}
                    </Box>
                </Collapse>
            </Box>
            <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }} >
                <Box bgcolor="background.paper" flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={1} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant='h6'>{previewingFileDetails?.sample_material_format}</Typography>
                        <Box display="flex" gap="10px" alignItems="center">
                            <IconButton onClick={() => { setPreviewFile( null ) }}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding="20px" height="300px">
                        <DocumentViewer type={previewingFileDetails?.sample_material_format?.split( "." ).pop()} loadingText="Loading material..." setFile={setPreviewFile} file={previewFile} />
                    </Box>
                </Box>
            </Drawer>
        </Card>
    )
} )

export default ControlPanelSettingBox